import axios from 'axios';
import { isNativeMobile } from './platform';
import { getSessionToken, setSessionToken, removeSessionToken } from './sessionStorage';

/**
 * Configure axios for mobile and web platforms
 * - On web: Uses cookies (withCredentials)
 * - On mobile: Uses Authorization header with session token
 */
export const configureAxios = () => {
  // Always set base URL if provided
  if (process.env.REACT_APP_API_BASE_URL) {
    axios.defaults.baseURL = process.env.REACT_APP_API_BASE_URL;
  }

  const isMobile = isNativeMobile();
  console.log('[AXIOS] Platform detection - isNativeMobile:', isMobile);

  if (isMobile) {
    // Mobile: Use Authorization header for session management
    axios.defaults.withCredentials = false;

    // Request interceptor to add session token to headers
    axios.interceptors.request.use(
      async (config) => {
        const token = await getSessionToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );

    // Response interceptor to handle session token from login response
    axios.interceptors.response.use(
      async (response) => {
        // If login response contains a session token, store it
        if (response.data && response.data.session_token) {
          await setSessionToken(response.data.session_token);
        }
        return response;
      },
      async (error) => {
        // If 401 Unauthorized, clear session token
        if (error.response && error.response.status === 401) {
          await removeSessionToken();
        }
        return Promise.reject(error);
      }
    );
  } else {
    // Web: Use cookies for session management
    axios.defaults.withCredentials = true;
    console.log('[AXIOS] Web mode - withCredentials:', axios.defaults.withCredentials);
  }

  // Issue #292: Response interceptor to report frontend errors to backend
  // Uses fetch() instead of axios to avoid recursive interceptor calls
  axios.interceptors.response.use(
    (response) => response,
    (error) => {
      const isNetworkError = !error.response;
      const isServerError = error.response && error.response.status >= 500;

      if (isNetworkError || isServerError) {
        try {
          const baseUrl = process.env.REACT_APP_API_BASE_URL || '';
          fetch(`${baseUrl}/api/report-frontend-error`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              error_type: isNetworkError ? 'network_error' : 'server_error',
              message: error.message || 'Unknown error',
              url: typeof window !== 'undefined' ? window.location.href : null,
              endpoint: error.config?.url || null,
              status_code: error.response?.status || null,
              user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
              timestamp: new Date().toISOString(),
            }),
          }).catch(() => {}); // Ignore if this also fails
        } catch (e) {
          // Silently ignore — we can't report if backend is fully down
        }
      }

      return Promise.reject(error);
    }
  );
};

/**
 * Handle logout for mobile platform
 * Clears the stored session token
 */
export const mobileLogout = async () => {
  if (isNativeMobile()) {
    await removeSessionToken();
  }
};
